import { initMatomo } from "@certible/use-matomo";
import * as Sentry from "@sentry/vue";
import { createHead } from "@unhead/vue/client";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp } from "vue";
import { isNavigationFailure } from "vue-router";

import { setLocale } from "@/i18n.ts";
import router from "@/router.ts";
import { auth0Init } from "@/utils/auth.ts";
import { i18nInstance } from "@/utils/i18n.ts";

import App from "./App.vue";

import "@/utils/customElements.ts";

const app = createApp(App);

const i18n = i18nInstance;
//@ts-ignore -- somehow throws error, don't really know how to fix, suggested solutions haven't worked or led to more errors
i18n.global.locale.value = navigator.language.split("-")[0];

const head = createHead();
const pinia = createPinia();

// @ts-ignore
if (!import.meta.env.DEV) {
  Sentry.init({
    app,
    environment: import.meta.env.VITE_ENVIRONMENT,
    dsn: "https://478e34bee2e5465597ed12b7e76ea285@o1063741.ingest.sentry.io/6330661",
    integrations: [Sentry.browserTracingIntegration({ router })],
    ignoreErrors: [
      /Failed to fetch dynamically imported module:/,
      /Icon with name (.+?) not found\. You might want to check the whether the file exists under this name/,
      /Constructor for (.+?) was not found/,
    ],
    // Tracing
    tracesSampleRate: 0.5,
  });
}

pinia.use(piniaPluginPersistedstate);
app.use(head);
app.use(pinia);
app.use(i18n);

// @ts-ignore
await setLocale(i18n, navigator.language?.split("-")[0] ?? "de");

/**
 * Initialize Router
 *
 * IMPORTANT:
 * Auth0 recommends installing the router before the Auth0 plugin so the plugin
 * can reliably hook into navigation events.
 */
app.use(router);

/**
 * Initialize Auth0
 */
// If TypeScript complains here, re-introduce a typed cast (avoid `any` if possible).
app.use(auth0Init);

/**
 * Initialize Matomo
 *
 * We intentionally only specify non-default options here to keep the intent
 * clear. (Defaults are handled by @certible/use-matomo / Matomo itself.)
 */
if (import.meta.env.VITE_ENABLE_MATOMO === "true") {
  const matomo = initMatomo({
    host: import.meta.env.VITE_MATOMO_URL || "https://used-test.dpa.com",
    siteId: Number(import.meta.env.VITE_MATOMO_SITE_ID) || 15,
    disableCookies: true,
    trackRouter: true,
  });
  // Keep the return value referenced to avoid unused-variable warnings
  void matomo;
}

// Ensure router has resolved async hooks before mounting.
await router.isReady().catch((err) => {
  if (!isNavigationFailure(err)) throw err;
});

app.mount("#app");
